import React from 'react';
import {
  CategoryIcon,
  AssignmentIcon,
  EventIcon,
  PersonIcon,
  EmailIcon,
  BusinessIcon,
  LocationIcon,
  DescriptionIcon,
  ComputerIcon,
  BuildIcon
} from '../icons/ViewTicketsIcons';
import { getStatusColor } from '../../utils/viewTicketsUtils';

const TicketDetailsModal = ({
  isOpen,
  onClose,
  selectedTicket,
  locationInfo
}) => {
  if (!isOpen || !selectedTicket) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto font-poppins">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-900">
              Ticket Details #{selectedTicket.id}
            </h3>
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(selectedTicket.status)}`}>
              {selectedTicket.status.replace('_', ' ')}
            </span>
          </div>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-6">
              {/* Basic Information */}
              <div>
                <h4 className="text-base font-semibold text-gray-900 mb-4">
                  Basic Information
                </h4>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CategoryIcon className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-gray-900">Category</p>
                      <p className="text-gray-600">{selectedTicket.category}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <AssignmentIcon className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-gray-900">Tracking ID</p>
                      <p className="text-gray-600">{selectedTicket.trackingId}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <EventIcon className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-gray-900">Created At</p>
                      <p className="text-gray-600">{new Date(selectedTicket.createdAt).toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Requester Information */}
              <div>
                <h4 className="text-base font-semibold text-gray-900 mb-4">
                  Requester Information
                </h4>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <PersonIcon className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-gray-900">Name</p>
                      <p className="text-gray-600">{selectedTicket.requester?.name || 'Unknown'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <EmailIcon className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-gray-900">Email</p>
                      <p className="text-gray-600">{selectedTicket.requester?.email || 'No email provided'}</p>
                    </div>
                  </div>
                  {selectedTicket.requester?.department && (
                    <div className="flex items-start gap-3">
                      <BusinessIcon className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-gray-900">Department</p>
                        <p className="text-gray-600">{selectedTicket.requester.department}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Location Information */}
              <div>
                <h4 className="text-base font-semibold text-gray-900 mb-4">
                  Location Information
                </h4>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <LocationIcon className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-gray-900">Location</p>
                      <p className="text-gray-600">{`${locationInfo?.name || 'Unknown'} - ${locationInfo?.type} ${locationInfo?.level ? `- ${locationInfo.level}` : ''}`}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* Ticket Details */}
              <div>
                <h4 className="text-base font-semibold text-gray-900 mb-4">
                  Ticket Details
                </h4>
              <div className="space-y-3">
                {/* Common details first */}
                {(selectedTicket.details?.subject || selectedTicket.details?.documentSubject) && (
                  <div className="flex items-start gap-3">
                    <AssignmentIcon className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-gray-900">Subject</p>
                      <p className="text-gray-600">{selectedTicket.details?.subject || selectedTicket.details?.documentSubject}</p>
                    </div>
                  </div>
                )}
                
                {(selectedTicket.details?.message || selectedTicket.details?.documentMessage) && (
                  <div className="flex items-start gap-3">
                    <DescriptionIcon className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-gray-900">Message</p>
                      <p className="text-gray-600">{selectedTicket.details?.message || selectedTicket.details?.documentMessage}</p>
                    </div>
                  </div>
                )}
                
                {/* Troubleshooting specific details */}
                {selectedTicket.category === 'TROUBLESHOOTING' && (
                  <>
                    {selectedTicket.details?.typeOfEquipment && (
                      <div className="flex items-start gap-3">
                        <ComputerIcon className="w-5 h-5 text-blue-600 mt-0.5" />
                        <div>
                          <p className="font-medium text-gray-900">Equipment Type</p>
                          <p className="text-gray-600">{selectedTicket.details.typeOfEquipment}</p>
                        </div>
                      </div>
                    )}
                    {selectedTicket.details?.modelOfEquipment && (
                      <div className="flex items-start gap-3">
                        <BuildIcon className="w-5 h-5 text-blue-600 mt-0.5" />
                        <div>
                          <p className="font-medium text-gray-900">Equipment Model</p>
                          <p className="text-gray-600">{selectedTicket.details.modelOfEquipment}</p>
                        </div>
                      </div>
                    )}
                    {selectedTicket.details?.serialNo && (
                      <div className="flex items-start gap-3">
                        <AssignmentIcon className="w-5 h-5 text-blue-600 mt-0.5" />
                        <div>
                          <p className="font-medium text-gray-900">Serial Number</p>
                          <p className="text-gray-600">{selectedTicket.details.serialNo}</p>
                        </div>
                      </div>
                    )}
                    {selectedTicket.details?.specificProblem && (
                      <div className="flex items-start gap-3">
                        <DescriptionIcon className="w-5 h-5 text-blue-600 mt-0.5" />
                        <div>
                          <p className="font-medium text-gray-900">Problem Description</p>
                          <p className="text-gray-600">{selectedTicket.details.specificProblem}</p>
                        </div>
                      </div>
                    )}
                  </>
                )}
                
                {/* Account Management specific details */}
                {selectedTicket.category === 'ACCOUNT_MANAGEMENT' && (
                  <>
                    {selectedTicket.details?.accountType && (
                      <div className="flex items-start gap-3">
                        <PersonIcon className="w-5 h-5 text-blue-600 mt-0.5" />
                        <div>
                          <p className="font-medium text-gray-900">Account Type</p>
                          <p className="text-gray-600">{selectedTicket.details.accountType}</p>
                        </div>
                      </div>
                    )}
                    {selectedTicket.details?.actionType && (
                      <div className="flex items-start gap-3">
                        <BuildIcon className="w-5 h-5 text-blue-600 mt-0.5" />
                        <div>
                          <p className="font-medium text-gray-900">Action Type</p>
                          <p className="text-gray-600">{selectedTicket.details.actionType}</p>
                        </div>
                      </div>
                    )}
                  </>
                )}
                
                {/* Technical Assistance specific details */}
                {selectedTicket.category === 'TECHNICAL_ASSISTANCE' && selectedTicket.details?.taType && (
                  <div className="flex items-start gap-3">
                    <BuildIcon className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-gray-900">Assistance Type</p>
                      <p className="text-gray-600">{selectedTicket.details.taType}</p>
                    </div>
                  </div>
                )}
                
                {/* Document Upload specific details */}
                {selectedTicket.category === 'DOCUMENT_UPLOAD' && selectedTicket.details?.documentType && (
                  <div className="flex items-start gap-3">
                    <AssignmentIcon className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-gray-900">Document Type</p>
                      <p className="text-gray-600">{selectedTicket.details.documentType}</p>
                    </div>
                  </div>
                )}
                
                {/* File attachment if available */}
                {selectedTicket.details?.fileUrl && (
                  <div className="flex items-start gap-3">
                    <AssignmentIcon className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-gray-900">Attachment</p>
                      <a 
                        href={selectedTicket.details.fileUrl} 
                        target="_blank" 
                        rel="noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        {selectedTicket.details.fileName || 'View attached file'}
                      </a>
                    </div>
                  </div>
                )}
              </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-end p-6 pt-0">
          <button 
            onClick={onClose}
            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-poppins"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TicketDetailsModal;
